import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PillBadge from './PillBadge';
import SoftwareIcons from './SoftwareIcons';
import YouTubeModal from './YouTubeModal';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = ({ userData }) => {
  // Removed the GSAP rotation animation - SoftwareIcons handles its own rotation
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const videoUrl = 'https://youtu.be/NmS22FfrTHA?si=0JwND-uLv2T6wUmb';

  const openYouTubeModal = () => setIsYouTubeModalOpen(true);
  const closeYouTubeModal = () => setIsYouTubeModalOpen(false);

  return (
    <section 
      id="home" 
      className="relative w-full flex flex-col items-center justify-start bg-black pt-16 sm:pt-24 md:pt-28 lg:pt-32"
      style={{
        perspective: '1200px',
        transformStyle: 'preserve-3d',
        zIndex: 1
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-20 pb-6 sm:pb-12">
        {/* Greeting Bubble */}
        <PillBadge
          text={
            userData ? (
              <span className="text-sm sm:text-base">
                <span className="text-white">👋 Hi {userData.name.split(' ')[0]}</span>
                <span className="text-[#999999]">, Glad you're here</span>
              </span>
            ) : (
              <span className="text-sm sm:text-base">
                <span className="text-white">👋 Hello,</span>
                <span className="text-[#999999]"> Glad you're here</span>
              </span>
            )
          }
          className="mb-6 sm:mb-8 lg:mb-10"
        />

        {/* Main Headlines */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white tracking-tight leading-tight sm:leading-normal px-2 sm:px-0 max-w-xs sm:max-w-none mx-auto"
        >
          <span className="text-white block mb-1">I'm Prabhakar Pandey,</span>
          <span className="text-white block mb-2">Connecting through visuals,</span>
          <span className="text-[#999999] block">16+ years in</span>
          <span className="text-[#999999] block">Editing, Motion Graphics & Live Events.</span>
        </motion.h1>

        {/* Email Button */}
        {/* <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          whileTap={{ scale: 0.95 }}
          whileHover="hover"
          className="bg-black border border-white/60 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-medium hover:bg-white hover:text-black transition-all duration-300 flex items-center space-x-2 mx-auto mt-6 sm:mt-8"
        >
          <span>Email me</span>
          <span className="relative inline-flex w-3 h-3 sm:w-4 sm:h-4 overflow-hidden">
            <motion.svg
              className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              variants={{ hover: { x: [0, 8], opacity: [1, 0] } }}
              transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
            </motion.svg>
            <motion.svg
              className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              initial={{ x: -8, opacity: 0 }}
              variants={{ hover: { x: [ -8, 0, -8 ], opacity: [0, 1, 0] } }}
              transition={{ duration: 1.0, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
            </motion.svg>
          </span>
        </motion.button>  */}
      </div>

      {/* GSAP Scroll-Controlled Rotating Image with 3D Layering */}
       <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: 1, 
          scale: 1
        }}
        transition={{ duration: 1, delay: 0.6 }}
        className="relative w-56 h-56 sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] xl:w-[600px] xl:h-[600px] mx-auto mb-6 sm:mb-8"
        style={{
          perspective: '1000px',
          transformStyle: 'preserve-3d',
          zIndex: 1
        }}
        id="rotating-icons-container"
      >
        <div
          className="pointer-events-none absolute bottom-[-45px] left-1/2 -translate-x-1/2 z-30 w-[150%] h-[95%] rounded-full"
          style={{
            background: 'radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 35%, rgba(0,0,0,0.6) 55%, rgba(0,0,0,0) 75%)'
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-full"
          style={{
            background: 'radial-gradient(circle at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.4) 78%, rgba(0,0,0,0.75) 98%)'
          }}
        />
        <div className="relative w-full h-full">
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          >
           
          </div>
          <SoftwareIcons />
        </div>
        
        {/* Central animated button - Above everything */}
        <div className="absolute top-1/2 left-1/2 -translate-y-1/2 z-50 pointer-events-none" style={{ transform: 'translate(calc(-50% + 0.5rem), calc(-50% + 1rem))' }}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={openYouTubeModal}
            className="bg-transparent border-none text-white w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24
                       rounded-full pointer-events-auto relative"
          >
            {/* VideoVertical Icon */}
            <motion.div 
              className="absolute inset-0 flex items-center justify-center"
              animate={{ 
                scale: [1, 1.05, 1],
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 2C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H12.98" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.89062 2.52002V21.48" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16.8906 2.52002V21.48" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.88906 6.96997H2.53906" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.8893 12H2.0293" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.89047 16.97H2.48047" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21.8891 6.96997H17.5391" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21.8893 12H17.0293" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16.9707 12H5.9707" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21.8905 16.97H17.4805" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          </motion.button>
        </div>
      </motion.div>
      
      {/* YouTube Modal */}
      <YouTubeModal isOpen={isYouTubeModalOpen} onClose={closeYouTubeModal} videoUrl={videoUrl} />
    </section>
  );
};

export default HeroSection;
